import { useApolloClient } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import * as React from 'react';

import { evictHouseholdNetWorth } from '../providers/apolloCache';

import { useFeatureGates } from './useFeatureGates';

/**
 * Drops the cached household net worth when the viewer's investments gate flips mid-session.
 *
 * The server counts investment accounts in the rollup on that gate, and the accounts widget
 * lists them on it, so a total cached under the old gate stops matching the sections the
 * moment a refetched gate set lands. Mount it once in the app shell: a gate set only changes
 * while some `useFeatureGates` reader is mounted, and a shell-level one sees every change,
 * including those that land while the widget is not on screen.
 *
 * @returns Nothing; the hook exists for its cache effect.
 */
export const useNetWorthGateEviction = () => {
  const client = useApolloClient();
  const { flags, resolved } = useFeatureGates();
  const includeInvestments = flags.has(FeatureGate.InvestmentAccounts);
  const lastResolvedGate = React.useRef<boolean | null>(null);

  React.useEffect(() => {
    if (!resolved) {
      return;
    }
    if (lastResolvedGate.current !== null && lastResolvedGate.current !== includeInvestments) {
      evictHouseholdNetWorth(client.cache);
    }
    lastResolvedGate.current = includeInvestments;
  }, [client, includeInvestments, resolved]);
};
